import { useEffect } from 'react';
import { buildCanonicalUrl } from '../seo/pageMeta';

const updateTag = (selector, attributes, tagName = 'meta') => {
  const head = typeof document !== 'undefined' ? document.head : null;
  if (!head) return () => {};

  let element = head.querySelector(selector);
  const created = !element;

  if (!element) {
    element = document.createElement(tagName);
    head.appendChild(element);
  }

  const previousAttributes = {};
  Object.entries(attributes).forEach(([key, value]) => {
    previousAttributes[key] = element.getAttribute(key);
    if (value === undefined || value === null) {
      element.removeAttribute(key);
    } else {
      element.setAttribute(key, value);
    }
  });

  return () => {
    Object.entries(previousAttributes).forEach(([key, value]) => {
      if (value === null || value === undefined) {
        element.removeAttribute(key);
      } else {
        element.setAttribute(key, value);
      }
    });

    if (created) {
      head.removeChild(element);
    }
  };
};

const removeTags = (selector) => {
  const head = typeof document !== 'undefined' ? document.head : null;
  if (!head) return () => {};

  const removed = Array.from(head.querySelectorAll(selector));
  removed.forEach((element) => head.removeChild(element));

  return () => {
    removed.forEach((element) => head.appendChild(element));
  };
};

// Scripts this component appended at runtime; everything else in the head arrived with
// the served HTML.
const runtimeStructuredData = new WeakSet();

// react-snap bakes each prerendered page's JSON-LD into its static HTML, and the SPA
// fallback serves the homepage shell for routes that were never prerendered — so a deep
// route can boot carrying the homepage @graph. Drop what the document was served with;
// runtime scripts stay, since each is owned by its own Seo instance's cleanup. Scoped to
// scripts this component wrote (react-snap captures the attribute into the snapshot), so
// JSON-LD from anywhere else is left alone.
const clearInheritedStructuredData = () => {
  const head = typeof document !== 'undefined' ? document.head : null;
  if (!head) return;

  Array.from(head.querySelectorAll('script[type="application/ld+json"][data-managed-by="seo-component"]'))
    .filter((element) => !runtimeStructuredData.has(element))
    .forEach((element) => head.removeChild(element));
};

// Same idea for <meta name="robots">: a noindex page's snapshot (e.g. /pricing,
// /auth/login) bakes the tag in, and updateTag reuses rather than owns it, so it
// would survive client-side navigation to an indexable page.
const runtimeRobotsMeta = new WeakSet();

const clearInheritedRobotsMeta = () => {
  const head = typeof document !== 'undefined' ? document.head : null;
  if (!head) return;

  Array.from(head.querySelectorAll('meta[name="robots"]'))
    .filter((element) => !runtimeRobotsMeta.has(element))
    .forEach((element) => head.removeChild(element));
};

const createStructuredData = (data) => {
  const head = typeof document !== 'undefined' ? document.head : null;
  if (!head) return () => {};

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-managed-by', 'seo-component');
  script.textContent = JSON.stringify(data);
  runtimeStructuredData.add(script);
  head.appendChild(script);

  return () => {
    head.removeChild(script);
  };
};

const resolveDocumentLanguage = (path, canonicalUrl) => {
  const value = canonicalUrl || path || '';

  try {
    const pathname = new URL(value, 'https://fideprep.ch').pathname;
    if (pathname === '/fr' || pathname.startsWith('/fr/')) return 'fr';
    if (pathname === '/de' || pathname.startsWith('/de/')) return 'de';
  } catch (error) {
    // Keep English as the safe default when a path cannot be parsed.
  }

  return 'en';
};

const Seo = ({
  title,
  description,
  path,
  canonicalUrl,
  type = 'website',
  twitterCard = 'summary',
  openGraphImage,
  siteName = 'FIDE Prep',
  structuredData,
  noindex = false,
  alternates,
}) => {
  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const cleanups = [];

    // Runs on every page, not just the ones with their own schema: the vocab and
    // flashcard routes ship no structuredData and would otherwise keep the inherited
    // homepage graph. Stale by definition, so it is not restored on cleanup.
    clearInheritedStructuredData();
    clearInheritedRobotsMeta();

    const previousDocumentLanguage = document.documentElement.lang;
    document.documentElement.lang = resolveDocumentLanguage(path, canonicalUrl);
    cleanups.push(() => {
      document.documentElement.lang = previousDocumentLanguage;
    });

    const previousTitle = document.title;
    if (title) {
      document.title = title;
    }

    const descriptionCleanup = description
      ? updateTag('meta[name="description"]', {
          name: 'description',
          content: description,
        })
      : null;

    if (descriptionCleanup) cleanups.push(descriptionCleanup);

    const canonical = canonicalUrl || (path ? buildCanonicalUrl(path) : null);
    const canonicalCleanup = canonical
      ? updateTag(
          'link[rel="canonical"]',
          {
            rel: 'canonical',
            href: canonical,
          },
          'link',
        )
      : null;

    if (canonicalCleanup) cleanups.push(canonicalCleanup);

    const ogTitleCleanup = title
      ? updateTag('meta[property="og:title"]', {
          property: 'og:title',
          content: title,
        })
      : null;
    if (ogTitleCleanup) cleanups.push(ogTitleCleanup);

    const ogDescriptionCleanup = description
      ? updateTag('meta[property="og:description"]', {
          property: 'og:description',
          content: description,
        })
      : null;
    if (ogDescriptionCleanup) cleanups.push(ogDescriptionCleanup);

    const ogTypeCleanup = updateTag('meta[property="og:type"]', {
      property: 'og:type',
      content: type,
    });
    cleanups.push(ogTypeCleanup);

    const ogUrlCleanup = canonical
      ? updateTag('meta[property="og:url"]', {
          property: 'og:url',
          content: canonical,
        })
      : null;
    if (ogUrlCleanup) cleanups.push(ogUrlCleanup);

    const twitterTitleCleanup = title
      ? updateTag('meta[name="twitter:title"]', {
          name: 'twitter:title',
          content: title,
        })
      : null;
    if (twitterTitleCleanup) cleanups.push(twitterTitleCleanup);

    const twitterDescriptionCleanup = description
      ? updateTag('meta[name="twitter:description"]', {
          name: 'twitter:description',
          content: description,
        })
      : null;
    if (twitterDescriptionCleanup) cleanups.push(twitterDescriptionCleanup);

    const twitterCardCleanup = updateTag('meta[name="twitter:card"]', {
      name: 'twitter:card',
      content: twitterCard,
    });
    cleanups.push(twitterCardCleanup);

    const ogImageCleanup = openGraphImage
      ? updateTag('meta[property="og:image"]', {
          property: 'og:image',
          content: openGraphImage,
        })
      : null;
    if (ogImageCleanup) cleanups.push(ogImageCleanup);

    if (siteName) {
      cleanups.push(updateTag('meta[property="og:site_name"]', { property: 'og:site_name', content: siteName }));
    }

    // Mirrors <html lang>, which resolveDocumentLanguage has already derived from
    // the path — og:locale disagreeing with the document language is a common and
    // avoidable mismatch.
    const ogLocaleByLanguage = { fr: 'fr_CH', de: 'de_CH', en: 'en_GB' };
    const resolvedLocale = ogLocaleByLanguage[resolveDocumentLanguage(path, canonicalUrl)];
    if (resolvedLocale) {
      cleanups.push(updateTag('meta[property="og:locale"]', { property: 'og:locale', content: resolvedLocale }));
    }

    // hreflang only belongs on a page that is its own canonical. Topic views and
    // the exam-tips index canonicalise to another URL, so emitting the cluster
    // there makes the page's hreflang disagree with its canonical (Ahrefs:
    // "hreflang conflicts within page source code").
    const isSelfCanonical = canonical
      ? canonical === buildCanonicalUrl(typeof window !== 'undefined' ? window.location.pathname : path || '/')
      : true;

    // The SPA fallback is the homepage snapshot, so an unprerendered route arrives
    // carrying the homepage's own x-default. Clearing the inherited cluster first is
    // what makes the guard effective there — skipping our own tags is not enough.
    cleanups.push(removeTags('link[rel="alternate"][hreflang]'));

    if (Array.isArray(alternates) && isSelfCanonical) {
      alternates.forEach(({ hreflang, href }) => {
        if (!hreflang || !href) return;
        const cleanup = updateTag(
          `link[rel="alternate"][hreflang="${hreflang}"]`,
          { rel: 'alternate', hreflang, href },
          'link',
        );
        cleanups.push(cleanup);
      });
    }

    const structuredDataCleanup = structuredData ? createStructuredData(structuredData) : null;
    if (structuredDataCleanup) cleanups.push(structuredDataCleanup);

    const robotsCleanup = noindex
      ? updateTag('meta[name="robots"]', {
          name: 'robots',
          content: 'noindex',
        })
      : null;
    if (robotsCleanup) {
      runtimeRobotsMeta.add(document.head.querySelector('meta[name="robots"]'));
      cleanups.push(robotsCleanup);
    }

    return () => {
      cleanups.reverse().forEach((cleanup) => cleanup && cleanup());
      document.title = previousTitle;
    };
  }, [
    title,
    description,
    path,
    canonicalUrl,
    type,
    twitterCard,
    openGraphImage,
    siteName,
    structuredData,
    noindex,
    alternates,
  ]);

  return null;
};

export default Seo;
